import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1641939193329-7071068dc40f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxkYWlyeSUyMGZhcm0lMjBjb3dzJTIwcGFzdHVyZXxlbnwwfHx8fDE3ODM1OTg5NzB8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block relative w-1/2">
        <img src={BG} alt="Cows grazing" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[hsl(148,38%,16%)]/55" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-white/15 backdrop-blur">
              <Leaf className="h-6 w-6" />
            </div>
            <span className="font-display font-extrabold text-2xl tracking-tight">Farm Hand</span>
          </div>
          <div>
            <h2 className="font-display text-4xl font-extrabold tracking-tight leading-tight">Grow more grass.<br/>Feed smarter.</h2>
            <p className="mt-4 text-white/80 max-w-md leading-relaxed">
              Grass wedges, feed budgets and ration balancing for dairy and beef farmers — all in kg DM, hectares, litres and euro.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 grain flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="grid place-items-center h-10 w-10 rounded-lg bg-primary text-primary-foreground"><Leaf className="h-6 w-6" /></div>
            <span className="font-display font-extrabold text-2xl tracking-tight text-[hsl(148,38%,16%)]">Farm Hand</span>
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-[hsl(148,38%,16%)]">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">
            {mode === "login" ? "Sign in to manage your farms." : "Start managing grass and feed today."}
          </p>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}
            <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="auth-submit-button">
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          {mode === "login" && (
            <p className="text-xs text-muted-foreground mt-4 rounded-lg bg-secondary p-3">
              Demo login — <strong>farmer@farmhand.ie</strong> / <strong>farmhand123</strong>
            </p>
          )}

          <button
            type="button"
            data-testid="toggle-auth-mode"
            className="text-sm text-primary font-medium mt-4 hover:underline"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
          >
            {mode === "login" ? "Need an account? Sign up" : "Already have an account? Sign in"}
          </button>
        </div>
      </div>
    </div>
  );
}
